import React, { useState, useMemo } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import { SETTING_GROUPS } from '../config/settingsMenu';

// ─── SettingsLayout ───────────────────────────────────────────────────────────
const SettingsLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});

  // Which group contains the current route?
  const activeGroupKey = useMemo(() => {
    for (const g of SETTING_GROUPS) {
      if (g.items.some(i => location.pathname.startsWith(i.path))) return g.key;
    }
    return null;
  }, [location.pathname]);

  // Filter groups/items by role and search
  const visibleGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return SETTING_GROUPS.map(group => ({
      ...group,
      items: group.items.filter(item => {
        const hasRole = item.roles.includes(user?.role);
        const matchSearch = !q ||
          item.title.toLowerCase().includes(q) ||
          group.title.toLowerCase().includes(q);
        return hasRole && matchSearch;
      }),
    })).filter(g => g.items.length > 0);
  }, [search, user?.role]);

  const toggleGroup = (key) => {
    setCollapsed(prev => {
      // Determine if the group is currently open
      const currentlyOpen = isGroupOpen(key);
      // We want to set collapsed to the opposite of currentlyOpen
      // If it's currently open, we want to collapse it (true)
      // If it's currently closed, we want to expand it (false)
      return { ...prev, [key]: currentlyOpen };
    });
  };

  const isGroupOpen = (key) => {
    if (search.trim()) return true;
    if (collapsed[key] === true)  return false; // user closed it
    if (collapsed[key] === false) return true;  // user opened it
    return key === activeGroupKey;              // default: open if active
  };

  return (
    <>
      <div className="settings-container">
        {/* Sidebar */}
        <aside className="settings-sidebar border-end bg-body">
          {/* Header Area */}
          <div className="settings-header d-flex align-items-center px-3 py-3 border-bottom">
            <button 
              className="btn btn-link text-secondary p-1 me-2 hover-bg-gray rounded-circle" 
              onClick={() => navigate('/')}
              title="Back to main"
              style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <i className="bi bi-arrow-left"></i>
            </button>
            <h6 className="mb-0 fw-bold flex-fill">Settings</h6>
          </div>

          {/* Overview Link */}
          <div className="px-3 pt-3 pb-1">
            <NavLink
              to="/settings"
              end
              className={({ isActive }) =>
                `d-flex align-items-center gap-2 px-3 py-2 rounded-3 text-decoration-none fw-medium ${
                  isActive ? 'settings-item-active' : 'settings-item-default text-secondary'
                }`
              }
              style={{ fontSize: '0.875rem' }}
            >
              {({ isActive }) => (
                <>
                  <i className={`bi bi-grid-3x3-gap-fill ${isActive ? 'text-white' : 'text-primary opacity-75'}`}></i>
                  <span className="flex-fill">Overview</span>
                </>
              )}
            </NavLink>
          </div>

          {/* Quick Search */}
          <div className="px-3 py-2 border-bottom">
            <div className="input-group input-group-sm rounded-pill overflow-hidden settings-search-wrap">
              <span className="input-group-text bg-transparent border-0 pe-1">
                <i className="bi bi-search text-muted" style={{ fontSize: '0.8rem' }}></i>
              </span>
              <input
                className="form-control border-0 bg-transparent ps-1 shadow-none"
                placeholder="Find a setting..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
              {search && (
                <button
                  className="btn btn-link border-0 text-muted pe-2 shadow-none"
                  onClick={() => setSearch('')}
                  title="Clear search"
                  style={{ fontSize: '0.75rem' }}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </div>

          {/* Navigation Menu */}
          <div className="settings-nav-wrap flex-fill overflow-y-auto px-2 py-2">
            {visibleGroups.length === 0 ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center px-3">
                <i className="bi bi-search text-muted mb-2" style={{ fontSize: '1.5rem', opacity: 0.4 }}></i>
                <p className="text-muted small mb-1">No results for</p>
                <p className="small fw-semibold mb-2">"{search}"</p>
                <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setSearch('')}>
                  Clear search
                </button>
              </div>
            ) : (
              visibleGroups.map(group => {
                const open = isGroupOpen(group.key);
                return (
                  <div key={group.key} className="settings-group mb-1">
                    {/* ✅ Fix #4: Use <button> for accessibility */}
                    <button
                      className={`settings-group-btn d-flex align-items-center w-100 px-3 py-2 rounded-2 border-0 bg-transparent cursor-pointer transition-all ${open ? 'text-primary fw-semibold' : 'text-secondary fw-medium'}`}
                      style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left' }}
                      onClick={() => toggleGroup(group.key)}
                      aria-expanded={open}
                    >
                      <span className="flex-fill">{group.title}</span>
                      <i
                        className="bi bi-chevron-right ms-auto settings-chevron"
                        style={{
                          fontSize: '0.65rem',
                          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease'
                        }}
                      ></i>
                    </button>

                    <div className={`settings-items-container ${open ? 'is-open' : ''}`}>
                      <div className="settings-items-inner">
                        <div className="settings-items d-flex flex-column gap-1 pt-1 pb-2">
                          {group.items.map(item => (
                            <NavLink
                              key={item.path}
                              to={item.path}
                              className={({ isActive }) => `
                                d-flex align-items-center gap-2 px-3 py-2 rounded-3 text-decoration-none
                                ${isActive
                                  ? 'settings-item-active'
                                  : 'settings-item-default'}
                              `}
                              style={{ fontSize: '0.85rem' }}
                            >
                              {/* ✅ Fix #2: Use isActive from NavLink render prop */}
                              {({ isActive }) => (
                                <>
                                  <i className={`bi ${item.icon} ${isActive ? 'text-white' : 'text-primary opacity-75'}`}></i>
                                  <span className="flex-fill">{item.title}</span>
                                  {item.badge && (
                                    <span className={`badge rounded-pill ${isActive ? 'bg-white text-primary' : 'bg-danger'}`} style={{ fontSize: '0.58rem', padding: '2px 6px' }}>
                                      {item.badge}
                                    </span>
                                  )}
                                </>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* Content Area */}
        <main className="settings-content flex-fill overflow-y-auto">
          <div className="container-fluid p-4">
            <Outlet />
          </div>
        </main>
      </div>

      <style>{`
        .settings-container {
          display: flex;
          margin: -1rem;
          height: calc(100vh - 60px);
          overflow: hidden;
        }

        /* ✅ Fix #1: Use bg-body instead of bg-white for dark mode support */
        .settings-sidebar {
          width: 280px;
          min-width: 280px;
          display: flex;
          flex-direction: column;
          background: var(--bs-body-bg);
        }

        .settings-content {
          background: var(--bs-tertiary-bg, #f8f9fa);
        }

        .settings-search-wrap {
          background: var(--bs-tertiary-bg, #f5f5f5);
          border: 1px solid var(--bs-border-color) !important;
        }

        .settings-nav-wrap::-webkit-scrollbar { width: 4px; }
        .settings-nav-wrap::-webkit-scrollbar-thumb { background: var(--bs-border-color); border-radius: 4px; }

        /* Group button */
        .settings-group-btn { transition: color 0.2s ease; }
        .settings-group-btn:hover { color: var(--bs-body-color) !important; }

        /* Smooth accordion */
        .settings-items-container {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }
        .settings-items-container.is-open { grid-template-rows: 1fr; }
        .settings-items-inner { min-height: 0; }
        .settings-items { padding: 0 8px; }

        /* Nav item states */
        .settings-item-default {
          color: var(--bs-body-color);
          transition: background 0.15s, color 0.15s;
        }
        .settings-item-default:hover {
          background-color: var(--bs-primary-bg-subtle, rgba(13, 110, 253, 0.08));
          color: var(--bs-primary) !important;
        }
        .settings-item-default:hover .bi { color: var(--bs-primary) !important; opacity: 1 !important; }

        .settings-item-active {
          background: var(--bs-primary);
          color: white !important;
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.25);
        }

        .hover-bg-gray:hover { background-color: var(--bs-tertiary-bg); }
        .cursor-pointer { cursor: pointer; }

        /* Dark mode */
        [data-bs-theme='dark'] .settings-container { background: var(--dark-bg-primary); }
        [data-bs-theme='dark'] .settings-search-wrap { background: var(--dark-bg-tertiary); border-color: var(--dark-border-color) !important; }
        [data-bs-theme='dark'] .settings-item-default:hover { background-color: rgba(255,255,255,0.07); }
        [data-bs-theme='dark'] .settings-content { background: var(--dark-bg-primary); }

        @media (max-width: 992px) {
          .settings-sidebar { width: 250px; min-width: 250px; }
        }
        @media (max-width: 768px) {
          .settings-container { flex-direction: column; height: auto; }
          .settings-sidebar { width: 100%; border-right: none; border-bottom: 1px solid var(--bs-border-color); }
          .settings-nav-wrap { max-height: 40vh; }
        }
      `}</style>
    </>
  );
};

export default SettingsLayout;
